<script setup lang="ts">
  import { computed } from 'vue'
  import { Inbox, List } from 'lucide-vue-next'
  import type { Folder } from '../models/Folder'
  import type { FolderTreeNode } from '../models/FolderTreeNode'
  import FolderTreeNodeItem from './FolderTreeNodeItem.vue'

  const props = defineProps<{
    folders: Folder[]
    selectedFolderId: string | null
  }>()

  const emit = defineEmits<{
    'folder-selected': [folderId: string | null]
  }>()

  /**
   * Build a tree structure from the flat folder list.
   * Groups folders by parentFolderId and recursively builds children.
   */
  const folderTree = computed<FolderTreeNode[]>(() => {
    return buildTree(props.folders, null)
  })

  function buildTree(folders: Folder[], parentId: string | null): FolderTreeNode[] {
    return folders
      .filter((f) => (f.parentFolderId ?? null) === parentId)
      .sort((a, b) => a.sortOrder - b.sortOrder)
      .map((folder) => ({
        ...folder,
        children: buildTree(folders, folder.id),
        isExpanded: true
      }))
  }

  function handleFolderClick(folderId: string | null) {
    emit('folder-selected', folderId)
  }

  function isSelected(folderId: string | null): boolean {
    return props.selectedFolderId === folderId
  }
</script>

<template>
  <nav class="folder-navigation-panel" aria-label="Folder navigation">
    <!-- All Executions -->
    <div
      class="folder-item"
      :class="{ 'folder-item--active': isSelected(null) }"
      role="button"
      tabindex="0"
      aria-label="All Executions"
      @click="handleFolderClick(null)"
      @keydown.enter="handleFolderClick(null)"
      @keydown.space.prevent="handleFolderClick(null)"
    >
      <List class="folder-item__icon" :size="16" />
      <span class="folder-item__label">All Executions</span>
    </div>

    <!-- No Folder (unassigned) -->
    <div
      class="folder-item"
      :class="{ 'folder-item--active': isSelected('unassigned') }"
      role="button"
      tabindex="0"
      aria-label="No Folder"
      @click="handleFolderClick('unassigned')"
      @keydown.enter="handleFolderClick('unassigned')"
      @keydown.space.prevent="handleFolderClick('unassigned')"
    >
      <Inbox class="folder-item__icon" :size="16" />
      <span class="folder-item__label">No Folder</span>
    </div>

    <!-- Folder tree separator -->
    <div v-if="folderTree.length > 0" class="folder-separator" />

    <!-- Folder tree -->
    <FolderTreeNodeItem
      v-for="node in folderTree"
      :key="node.id"
      :node="node"
      :depth="0"
      :selected-folder-id="selectedFolderId"
      @folder-selected="handleFolderClick"
    />
  </nav>
</template>

<style scoped>
  .folder-navigation-panel {
    display: flex;
    flex-direction: column;
    gap: 2px;
    padding: 8px 0;
    min-width: 200px;
  }

  .folder-item {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 8px 12px;
    border-radius: 6px;
    cursor: pointer;
    user-select: none;
    transition: background-color 0.15s ease;
  }

  .folder-item:hover {
    background-color: var(--color-background-soft, #f5f5f5);
  }

  .folder-item:focus-visible {
    outline: 2px solid #1677ff;
    outline-offset: -2px;
  }

  .folder-item--active {
    background-color: #e6f4ff;
    font-weight: 500;
  }

  .folder-item--active:hover {
    background-color: #bae0ff;
  }

  .folder-item__icon {
    flex-shrink: 0;
    color: var(--color-text-secondary, #666666);
  }

  .folder-item--active .folder-item__icon {
    color: #1677ff;
  }

  .folder-item__label {
    font-size: 13px;
    line-height: 1.4;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .folder-separator {
    height: 1px;
    background-color: var(--color-border, #e8e8e8);
    margin: 4px 12px;
  }
</style>
